import { useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { useTransferOwnership } from '@/hooks/useTeam'
import { Loader2, AlertTriangle, Crown } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import type { TeamMember } from '@/hooks/useTeam'

interface TransferOwnershipModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  member: TeamMember | null
}

const CONFIRMATION_TEXT = 'TRANSFER'

export function TransferOwnershipModal({ open, onOpenChange, member }: TransferOwnershipModalProps) {
  const [confirmationInput, setConfirmationInput] = useState('')
  const { toast } = useToast()
  const transferMutation = useTransferOwnership()

  const handleTransfer = async () => {
    if (!member || confirmationInput !== CONFIRMATION_TEXT) return

    try {
      await transferMutation.mutateAsync(member.id)

      toast({
        title: 'Ownership transferred',
        description: `${member.user.full_name} is now the owner`,
      })

      setConfirmationInput('')
      onOpenChange(false)
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Failed to transfer ownership'
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    }
  }

  const handleClose = () => {
    if (!transferMutation.isPending) {
      setConfirmationInput('')
      onOpenChange(false)
    }
  }

  if (!member) return null

  const isConfirmed = confirmationInput === CONFIRMATION_TEXT

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Crown className="h-5 w-5 text-amber-500" />
            <DialogTitle>Transfer Ownership</DialogTitle>
          </div>
          <DialogDescription>
            This is a permanent action that will change your role to Admin
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* New Owner Info */}
          <div>
            <Label className="text-sm text-muted-foreground mb-2">New Owner</Label>
            <div className="flex items-center gap-3 p-3 border rounded-lg bg-muted/50">
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold">
                {member.user.full_name.charAt(0).toUpperCase()}
              </div>
              <div>
                <div className="font-medium">{member.user.full_name}</div>
                <div className="text-sm text-muted-foreground">{member.user.email}</div>
              </div>
            </div>
          </div>

          {/* Warning */}
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription className="space-y-2">
              <p className="font-semibold">This action cannot be undone!</p>
              <p className="text-sm">
                Transferring ownership means:
              </p>
              <ul className="text-sm list-disc list-inside space-y-1 ml-2">
                <li>You will become an Admin</li>
                <li><strong>{member.user.full_name}</strong> will become the Owner</li>
                <li>Only the new owner can transfer ownership again</li>
                <li>The new owner will have full control over the workspace</li>
              </ul>
            </AlertDescription>
          </Alert>

          {/* Confirmation Input */}
          <div className="space-y-2">
            <Label htmlFor="confirmation">
              Type <span className="font-mono font-bold">{CONFIRMATION_TEXT}</span> to confirm
            </Label>
            <Input
              id="confirmation"
              value={confirmationInput}
              onChange={(e) => setConfirmationInput(e.target.value.toUpperCase())}
              placeholder={CONFIRMATION_TEXT}
              disabled={transferMutation.isPending}
              className={isConfirmed ? 'border-green-500' : ''}
              autoComplete="off"
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={transferMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleTransfer}
            disabled={!isConfirmed || transferMutation.isPending}
          >
            {transferMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Transferring...
              </>
            ) : (
              <>
                <Crown className="mr-2 h-4 w-4" />
                Transfer Ownership
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
